import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { t } from '@lingui/macro';
import { Spinner, useToast } from '@toqio/toqio-web-components';
import { useBridge } from '../../bridge/useBridge';
import { PageContainer } from '../../shared/components';
import { type ApiError, parseApiError, uploadDelegates } from './api';
import {
  downloadBase64File,
  downloadTextFile,
  ERROR_REPORT_FILENAME,
  EXAMPLE_CSV,
  EXAMPLE_CSV_FILENAME,
  readFileAsBase64,
} from './csv';
import { Actions } from './components/Actions';
import { FlowHeader } from './components/FlowHeader';
import { Instructions } from './components/Instructions';
import { ResultSummary } from './components/ResultSummary';
import { MOBILE } from './components/styles';
import type { DelegateUploadAck } from './types';

/**
 * un-bulk-upload — a conference admin uploads a CSV of delegates (UN bulk
 * upload, Flowable page `BU_PG005`).
 *
 * A single screen with three mutually-exclusive body states driven by local
 * state (PRD §3) — NOT a `StepWizard` and no zod form (PRD §6):
 *   - `instructions` — two columns: instructions (left) + the upload controls
 *      (right: execute-payments checkbox, upload action, download-example link).
 *   - `uploading` — a Spinner while the synchronous validation is in flight.
 *   - `result` — the validation summary (totals + status tiles).
 *
 * The upload fires the moment a CSV is picked (no Submit button — PRD O-8). The
 * request carries only `{ file, executePayments }`; the backend derives
 * conference identity from the JWT (PRD §4/§7). The header conference name comes
 * from `init.input` (display-only — PRD O-14). `readiness: 'auto'`.
 */
const FLOW_ID = 'un-bulk-upload';

type Status = 'idle' | 'uploading';

const Root = styled.div`
  padding: 24px 0; /* horizontal gutter comes from PageContainer (.lum-web-container) */

  @media (max-width: ${MOBILE}) {
    padding: 16px 0;
  }
`;

const TwoCol = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 48px;

  @media (max-width: ${MOBILE}) {
    flex-direction: column;
    gap: 32px;
  }
`;

const Left = styled.div`
  flex: 0 0 616px;
  min-width: 0;

  @media (max-width: ${MOBILE}) {
    flex-basis: auto;
    width: 100%;
  }
`;

const Right = styled.div`
  flex: 0 0 353px;
  max-width: 100%;

  @media (max-width: ${MOBILE}) {
    flex-basis: auto;
    width: 100%;
  }
`;

const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 24px;
  min-height: 40vh;
`;

const HiddenInput = styled.input`
  display: none;
`;

export const UnBulkUploadFlow: React.FC = () => {
  const bridge = useBridge();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [executePayments, setExecutePayments] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [result, setResult] = useState<DelegateUploadAck | null>(null);

  const input = bridge.input as { companyCif?: unknown; companyName?: unknown };
  const companyCif =
    typeof input.companyCif === 'string' && input.companyCif.trim()
      ? input.companyCif.trim()
      : null;
  const companyName = typeof input.companyName === 'string' ? input.companyName.trim() : '';
  const title = companyCif ? t`Add delegates to ${companyCif}` : t`Add delegates to conference`;

  useEffect(() => {
    bridge.emit({
      type: 'step:change',
      flowId: FLOW_ID,
      stepId: 'upload',
      stepIndex: 0,
      totalSteps: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Host back signal (e.g. RN hardware back): from the result screen, return to
  // the instructions screen; only leave the embed when already on instructions.
  useEffect(
    () =>
      bridge.onBackRequest(() => {
        if (result) setResult(null);
        else bridge.emit({ type: 'cancel', flowId: FLOW_ID });
      }),
    [bridge, result],
  );

  // Web: screen 1 → 2 is local state (no route change), so a plain browser back
  // would exit the flow. Push a history entry when entering the result screen
  // and trap `popstate` so back returns to the instructions screen instead.
  const pushedRef = useRef(false);
  useEffect(() => {
    if (!result) {
      pushedRef.current = false;
      return undefined;
    }
    if (!pushedRef.current) {
      window.history.pushState(null, '', window.location.href);
      pushedRef.current = true;
    }
    const onPop = () => {
      pushedRef.current = false;
      setResult(null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [result]);

  const uploadErrorMessage = (err: ApiError): string => {
    if (err.status === 403 || err.errorCode === 'ACCESS_DENIED') {
      return t`You don't have permission to upload delegates for this conference.`;
    }
    if (err.status === 401) {
      return t`Your session has expired. Please reload the page and sign in again.`;
    }
    if (err.status === 400) {
      return t`We couldn't process your file. Please check it matches the example format and try again.`;
    }
    return t`Something went wrong while uploading your file. Please try again.`;
  };

  // Auto-upload on file selection (PRD §2 phase 2 / O-8). On failure we toast
  // (transient action error) and stay on the instructions screen.
  const handleFileSelected = async (files: FileList | null): Promise<void> => {
    const file = files?.[0];
    if (!file) return;

    const isCsv = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
    if (!isCsv) {
      toast.error(
        'un-bulk-upload-file-type',
        t`Only CSV files are accepted. Please choose a .csv file.`,
      );
      return;
    }

    setStatus('uploading');
    bridge.emit({ type: 'telemetry', event: 'un-bulk-upload.upload.started' });
    try {
      const base64 = await readFileAsBase64(file);
      const ack = await uploadDelegates({ file: base64, executePayments });
      setResult(ack);
      bridge.emit({
        type: 'telemetry',
        event: 'un-bulk-upload.upload.succeeded',
        props: {
          total: ack.totalLinesCount,
          valid: ack.validLinesCount,
          invalid: ack.wrongValidationCount,
        },
      });
    } catch (e) {
      const err = parseApiError(e);
      toast.error('un-bulk-upload-upload-error', uploadErrorMessage(err));
      bridge.emit({
        type: 'telemetry',
        event: 'un-bulk-upload.upload.failed',
        props: { status: err.status ?? null, errorCode: err.errorCode },
      });
    } finally {
      setStatus('idle');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadExample = (): void => {
    downloadTextFile(EXAMPLE_CSV_FILENAME, EXAMPLE_CSV);
    bridge.emit({ type: 'telemetry', event: 'un-bulk-upload.example.downloaded' });
  };

  const handleDownloadErrorReport = (): void => {
    if (!result?.errorFile) return;
    const ok = downloadBase64File(ERROR_REPORT_FILENAME, result.errorFile);
    if (!ok) {
      toast.error(
        'un-bulk-upload-error-report',
        t`We couldn't generate the error report. Please try again.`,
      );
      return;
    }
    bridge.emit({ type: 'telemetry', event: 'un-bulk-upload.errorReport.downloaded' });
  };

  return (
    <PageContainer>
      <Root>
        <FlowHeader title={title} subtitle={companyName || undefined} />

        {result ? (
          <ResultSummary
            result={result}
            hasErrorReport={Boolean(result.errorFile)}
            onDownloadErrorReport={handleDownloadErrorReport}
          />
        ) : status === 'uploading' ? (
          <Centered>
            <Spinner />
          </Centered>
        ) : (
          <TwoCol>
            <Left>
              <Instructions companyCif={companyCif ?? t`the conference`} />
            </Left>
            <Right>
              <Actions
                executePayments={executePayments}
                onExecutePaymentsChange={setExecutePayments}
                onUploadClick={() => fileInputRef.current?.click()}
                onDownloadExample={handleDownloadExample}
              />
            </Right>
          </TwoCol>
        )}

        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          data-testid="un-bulk-upload-file-input"
          onChange={(e) => void handleFileSelected(e.target.files)}
        />
      </Root>
    </PageContainer>
  );
};

export default UnBulkUploadFlow;
